import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function RafflesSentosaButlerPackagePageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Raffles Sentosa Singapore "The Butler Did It" Package' }
  ];

  const article = {
    category: 'High-Value Services & Pricing',
    date: 'October 15, 2025',
    title: 'Raffles Sentosa Singapore "The Butler Did It" Package',
    subtitle: 'Ultra-Luxury Experience at Approximately $5,000 Per Night',
    heroImage: '/images/generated/jba-fs-117-news_raffles-sentosa-butler-package.jpg',
    lead: 'Raffles Sentosa Singapore\'s announced "The Butler Did It" 4-day 3-night bespoke luxury package is priced at approximately $5,000 per night, offering the ultimate personalization experience centered on dedicated butler service. The specialized new service form of wellbeing butler is also attracting attention.',
    comment: 'Raffles Sentosa Singapore\'s "The Butler Did It" package is deeply noted by our association as a groundbreaking case clearly showing that butler service is recognized as the core value of ultra-luxury experiences. Particularly, the specialization direction of "wellbeing butler" demonstrates the evolution and possibilities of the butler profession. The fact that dedicated butler service is incorporated as an essential element at approximately $5,000 per night pricing demonstrates to the world the value of butlers as the ultimate in personalization. Such deployment of international high-value services suggests possibilities for similar service development in Japan\'s wealthy market, and we will actively work toward establishing next-generation butler services comprehensively supporting physical and mental health and wellbeing.',
    related: [
      { to: '/en/news/raffles-butler-campaign', image: '/images/generated/jba-fs-en-018-news_raffles-sentosa-butler-package.jpg', aspect: 'aspect-[3/4]', alt: 'Related article: Raffles "The Butler Did It" Global Campaign', title: 'Raffles "The Butler Did It" Global Campaign', desc: 'Luxury hotel butler service innovation' },
      { to: '/en/news/ai-hospitality-revolution', image: '/images/generated/jba-fs-en-019-news_raffles-sentosa-butler-package.jpg', aspect: 'aspect-[4/3]', alt: 'Related article: AI Hospitality Revolution: 5 Transformations', title: 'AI Hospitality Revolution: 5 Transformations', desc: 'The future of butler services opened by AI' }
    ]
  };

  const sources = [
    { label: 'Source', value: 'TTG Asia', link: false },
    { label: 'Package Coverage', value: 'https://www.ttgasia.com/2025/10/15/raffles-sentosa-singapore-unveils-bespoke-four-day-luxury-escape/', link: true },
    { label: 'All-Villa Resort Announcement', value: 'https://www.ttgasia.com/2025/05/19/raffles-sentosa-singapore-unveils-first-ever-all-villa-resort/', link: true },
    { label: 'Resort Debut Data', value: 'https://press.accor.com/raffles-sentosa-singapore-debuts-a-glamorous-resort-experience-in-one-of-the-worlds-most-dynamic-destinations', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];

  const toc = [
    { id: 's-pricing', label: 'Package Overview and Pricing' },
    { id: 's-resort', label: 'Debut as an All-Villa Resort' },
    { id: 's-dining', label: 'Curated Dining Experience' },
    { id: 's-bespoke', label: 'Bespoke Experience Program' },
    { id: 's-lineup', label: 'Resort Layout and Five Dining Venues' },
    { id: 's-wellbeing', label: 'Wellbeing Butler Service' }
  ];

  return (
    <Layout>
      <title>Raffles Sentosa Singapore "The Butler Did It" Package | Japan Butler Association</title>
      <meta name="description" content="Raffles Sentosa Singapore unveils a 4-day bespoke luxury package from about $5,000 per night, centered on dedicated butler service and a new wellbeing butler role." />
      <link rel="canonical" href="https://butler-association.jp/en/news/raffles-sentosa-butler-package" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/raffles-sentosa-butler-package" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/raffles-sentosa-butler-package" />

      <ArticleHero eyebrow="News / Luxury" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                '"The Butler Did It" is a 4-day, 3-night bespoke package. It targets Garden One-Bedroom Pool Villas from SGD 4,985 (approx. $5,000) per night, with service charges and taxes additional',
                'The setting, Raffles Sentosa Singapore, opened on March 1, 2025 as the brand\'s first-ever all-villa resort, with 62 pool villas across roughly 100,000 square metres',
                'Alongside the dedicated butler, a "wellbeing butler" working with the resort\'s wellness team is drawing attention as a new form of service'
              ]}
            />

            {/* 01: Pricing (existing body, unchanged) */}
            <SectionHeading id="s-pricing" en="Pricing">Package Overview and Pricing</SectionHeading>
            <div className="space-y-6">
              <p className={P}>"The Butler Did It" package targets Garden One-Bedroom Pool Villas with ultra-premium pricing starting from SGD 4,985 (approximately $5,000) per night. Service charges and taxes are additional to this price.</p>
              <p className={P}>Guests are greeted by private limousine and accompanied by a dedicated Raffles butler throughout their stay. Villas are equipped with private pools, garden terraces, and spacious living areas, ensuring completely private accommodation.</p>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-sentosa-butler-package-ttgasia.jpg"
              alt="Screenshot of the TTG Asia article covering the Raffles Sentosa Singapore bespoke four-day luxury package"
              media="TTG Asia"
              url="https://www.ttgasia.com/2025/10/15/raffles-sentosa-singapore-unveils-bespoke-four-day-luxury-escape/"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="Centered on a dedicated butler, the ultimate personalization experience at about $5,000 per night." cite="Raffles Sentosa Singapore &quot;The Butler Did It&quot;" />
            </div>

            {/* Augmented: All-Villa Resort */}
            <SectionHeading id="s-resort" en="All-Villa Resort">Debut as an All-Villa Resort</SectionHeading>
            <div className="space-y-6">
              <p className={P}>The setting for this package, Raffles Sentosa Singapore, opened on <Stat>March 1, 2025</Stat> as the brand's first-ever all-villa resort. Across roughly <Stat>100,000</Stat> square metres it lays out <Stat>62</Stat> private pool villas, delivering a tranquil, residence-like stay quite unlike an urban resort.</p>
              <p className={P}>The all-villa configuration itself provides the foundation for complete privacy and personalized service by a dedicated butler, and this package is positioned to make the most of the resort's character.</p>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-sentosa-butler-package-ttgasia2.jpg"
              alt="Screenshot of the TTG Asia article reporting that Raffles Sentosa Singapore was unveiled as the brand's first-ever all-villa resort"
              media="TTG Asia"
              url="https://www.ttgasia.com/2025/05/19/raffles-sentosa-singapore-unveils-first-ever-all-villa-resort/"
            />

            {/* 02: Dining (existing body, unchanged) */}
            <SectionHeading id="s-dining" en="Dining">Curated Dining Experience</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Breakfast is served in-villa or at modern Italian restaurant "Empire Grill," with curated tasting menus including Empire Grill's modern Italian cuisine and "Royal China's" Cantonese cuisine by Executive Chef Ling Heng Yao.</p>
              <p className={P}>These dining experiences are designed as gastronomic journeys customized based on deep understanding of guests' preferences and cultural backgrounds, beyond mere meal provision.</p>
            </div>

            {/* 03: Bespoke (existing body, unchanged) */}
            <SectionHeading id="s-bespoke" en="Bespoke">Bespoke Experience Program</SectionHeading>
            <div className="space-y-6">
              <p className={P}>The experience program includes luxurious travel experiences touring Sentosa Island by private Rolls Royce, signature cocktail "Raffles Sentosa Sling" tasting sessions, and fragrance-making workshops creating bespoke perfumes tailored to personal preferences.</p>
              <p className={P}>As the stay's finale, a private yacht charter to Sisters' Islands is provided, offering the ultimate finale experience of enjoying champagne and gourmet canapés at sunset.</p>
            </div>

            {/* Augmented: Resort Lineup */}
            <SectionHeading id="s-lineup" en="Resort Lineup">Resort Layout and Five Dining Venues</SectionHeading>
            <div className="space-y-6">
              <p className={P}>The resort houses a total of <Stat>5</Stat> restaurants and lounges, including Empire Grill and Royal China featured in the package. The Japanese "Iyasaka by Hashida," the "Raffles Room" and the "Chairman's Room" round out the lineup, enabling a wide range of gastronomic experiences.</p>
              <p className={P}>By having a dedicated butler seamlessly arrange these venues, scattered across the expansive grounds, to each guest's preferences, the value of the package is further elevated.</p>
            </div>

            {/* 04: Wellbeing Butler (existing body, unchanged) */}
            <SectionHeading id="s-wellbeing" en="Wellbeing Butler">Wellbeing Butler Service</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Particularly noteworthy is the service provided by a dedicated wellbeing butler working with the resort's wellness team. This includes tailored spa treatments, nutritional guidance, and mindfulness practices.</p>
              <p className={P}>This wellbeing butler concept is attracting significant attention within the industry as a new direction showing specialization of traditional butler services. It serves as a specialist comprehensively supporting guests' physical and mental health and wellbeing, beyond mere service provision.</p>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Resort Opened</dt><dd className="text-[#333] mt-0.5">March 1, 2025</dd></div>
                  <div><dt className="text-[#8f7a56]">Villas</dt><dd className="text-[#333] mt-0.5">62 (all-villa)</dd></div>
                  <div><dt className="text-[#8f7a56]">Package Rate</dt><dd className="text-[#333] mt-0.5">From SGD 4,985/night</dd></div>
                  <div><dt className="text-[#8f7a56]">Category</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
